import { type FC, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { shortAddress } from 'dibs-crypto-utils/exports/formattingUtils';
import { ethereumPaymentSectionUrl } from 'dibs-crypto-utils/exports/constants';

import { WithWalletConnectionEnsuredModalBase } from './WithWalletConnectionEnsuredModalBase';
import { type MismatchedAddressContent_viewer$key } from './__generated__/MismatchedAddressContent_viewer.graphql';

export const MismatchedAddressContent: FC<{
    persistedAddress: string | null;
    viewer: MismatchedAddressContent_viewer$key;
}> = ({ persistedAddress, viewer: viewerRef }) => {
    const { account: connectedAddress, openConnectionForMismatch } = useWeb3Connection();

    const persistedShort = shortAddress(persistedAddress || '');
    const connectedShort = shortAddress(connectedAddress || '');

    useEffect(() => {
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'ethereum error displayed',
            label: 'mismatched address',
        });
    }, []);

    const viewer = useFragment(
        graphql`
            fragment MismatchedAddressContent_viewer on Viewer {
                ...WithWalletConnectionEnsuredModalBase_viewer
            }
        `,
        viewerRef
    );

    return (
        <WithWalletConnectionEnsuredModalBase
            onMetamaskConnect={openConnectionForMismatch}
            viewer={viewer}
            title={
                <FormattedMessage
                    id="nft.withWalletConnection.mismatchedAddress"
                    defaultMessage="Wallet Addresses Don't Match"
                />
            }
            warning={
                <FormattedMessage
                    id="nft.withWalletConnection.mismatchedAddress.bodyCopy1"
                    defaultMessage="The wallet address your are currenctly connected to <b>({connectedAddress})</b> does not match the wallet address associated with your 1stDibs account <b>({persistedAddress})</b>."
                    values={{
                        connectedAddress: connectedShort,
                        persistedAddress: persistedShort,
                        b: chunks => <strong>{chunks}</strong>,
                    }}
                />
            }
            paragraphs={[
                <FormattedMessage
                    id="nft.withWalletConnection.mismatchedAddress.bodyCopy2"
                    defaultMessage="Please switch your wallet address to <b>({persistedAddress})</b> in MetaMask in order to place a bid."
                    values={{
                        persistedAddress: persistedShort,
                        b: chunks => <strong>{chunks}</strong>,
                    }}
                />,
            ]}
            showWalletButtons
            useOpenWalletCopy
            footer={
                <FormattedMessage
                    id="nft.withWalletConnection.mismatchedAddress.bodyCopy3"
                    defaultMessage="If you'd like to connect a different address, you can disconnect from your current address in {paymentSettingsLink}."
                    values={{
                        paymentSettingsLink: (
                            <Link
                                href={ethereumPaymentSectionUrl}
                                dataTn="nft-payment-section-link"
                            >
                                <FormattedMessage
                                    id="nft.withWalletConnection.mismatchedAddress.paymentSettings"
                                    defaultMessage="payment settings"
                                />
                            </Link>
                        ),
                    }}
                />
            }
        />
    );
};
